import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React from 'react';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    aioChecklist: {
      /** Fügt einen Checklisten-Marker als Block in den Editor ein. */
      insertAioChecklist: (postId?: string) => ReturnType;
    };
  }
}

const MARKER_RE = /^\s*\[\[aio:checklist:([\w-]+)\]\]\s*$/;

const ChecklistChip: React.FC<any> = (props) => {
  const postId: string = props.node.attrs.postId || 'NEW';
  const label =
    postId === 'NEW' || postId === '0'
      ? 'Checkliste (wird beim Speichern angelegt)'
      : `Checkliste #${postId}`;
  return React.createElement(
    NodeViewWrapper,
    {
      as: 'div',
      className: 'aio-checklist-marker',
      contentEditable: false,
      'data-aio-checklist-marker': '',
      'data-post-id': postId,
      style: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        margin: '0.5rem 0',
        padding: '6px 10px',
        borderRadius: 4,
        border: '1px dashed #1976d2',
        backgroundColor: '#e3f2fd',
        color: '#0d47a1',
        fontFamily: 'monospace',
        fontSize: '0.85rem',
        userSelect: 'none' as const,
      },
    },
    React.createElement('span', { 'aria-hidden': 'true' }, '☑'),
    React.createElement('span', null, label),
  );
};

/**
 * TipTap-Extension: Marker-Block fuer interaktive Checklisten.
 *
 * - Im Editor wird der Marker als unauffaelliger Chip dargestellt (NodeView).
 * - Beim Serialisieren via `editor.getHTML()` wird ein `<p>[[aio:checklist:<id>]]</p>` ausgegeben,
 *   damit das Backend / der Sanitizer den Marker unveraendert beibehaelt.
 * - Beim Laden eines Bodies erkennt eine `parseHTML`-Regel Absaetze, deren Textinhalt der Marker ist,
 *   und konvertiert sie in den eigenen Block.
 */
export const ChecklistMarker = Node.create({
  name: 'aioChecklist',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      postId: {
        default: 'NEW',
        parseHTML: (element: HTMLElement) => element.getAttribute('data-post-id') || 'NEW',
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'p',
        priority: 60,
        getAttrs: (node: HTMLElement | string) => {
          if (typeof node === 'string') return false;
          const text = node.textContent ?? '';
          const m = text.match(MARKER_RE);
          if (!m) return false;
          return { postId: m[1] };
        },
      },
      {
        tag: 'div[data-aio-checklist-marker]',
        getAttrs: (node: HTMLElement | string) => {
          if (typeof node === 'string') return false;
          return { postId: node.getAttribute('data-post-id') || 'NEW' };
        },
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    const postId = (node.attrs.postId as string) || 'NEW';
    return [
      'p',
      mergeAttributes(HTMLAttributes),
      `[[aio:checklist:${postId}]]`,
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ChecklistChip);
  },

  addCommands() {
    return {
      insertAioChecklist:
        (postId = 'NEW') =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { postId },
          }),
    };
  },
});

export default ChecklistMarker;
